/**
 * Zustand store for CSV import wizard state
 */

import { create } from 'zustand'
import type {
  CSVImportStep,
  CSVUploadResponse,
  CSVImportResult,
  ColumnMappingConfig,
  ImportTemplateInfo,
  EntityLevel,
  TargetTable,
  KeyType,
  EditionMode,
} from '../types'

interface CSVImportState {
  // Current step
  step: CSVImportStep

  // Upload
  file: File | null
  entityLevel: EntityLevel
  uploadResponse: CSVUploadResponse | null

  // Column mapping
  columnMappings: ColumnMappingConfig[]
  targetTable: TargetTable
  keyColumn: string
  keyType: KeyType

  // Template
  selectedTemplate: ImportTemplateInfo | null

  // Edition settings
  referenceYear: number
  editionMode: EditionMode
  editionName: string
  targetEditionId: number | null

  // Save template
  saveAsTemplate: string | null
  templateProvider: string

  // Import result
  importResult: CSVImportResult | null
  jobId: string | null

  // Loading states
  isUploading: boolean
  isImporting: boolean

  // Error
  error: string | null

  // Actions
  setStep: (step: CSVImportStep) => void
  setFile: (file: File | null) => void
  setEntityLevel: (level: EntityLevel) => void
  setUploadResponse: (response: CSVUploadResponse | null) => void
  setColumnMappings: (mappings: ColumnMappingConfig[]) => void
  updateColumnMapping: (index: number, mapping: Partial<ColumnMappingConfig>) => void
  setTargetTable: (table: TargetTable) => void
  setKeyColumn: (column: string) => void
  setKeyType: (type: KeyType) => void
  setSelectedTemplate: (template: ImportTemplateInfo | null) => void
  applyTemplate: (template: ImportTemplateInfo, mappings: Record<string, ColumnMappingConfig>) => void
  setReferenceYear: (year: number) => void
  setEditionMode: (mode: EditionMode) => void
  setEditionName: (name: string) => void
  setTargetEditionId: (id: number | null) => void
  setSaveAsTemplate: (name: string | null) => void
  setTemplateProvider: (provider: string) => void
  setImportResult: (result: CSVImportResult | null) => void
  setJobId: (id: string | null) => void
  setUploading: (isUploading: boolean) => void
  setImporting: (isImporting: boolean) => void
  setError: (error: string | null) => void
  reset: () => void
}

const currentYear = new Date().getFullYear()

const initialState = {
  step: 'upload' as CSVImportStep,
  file: null,
  entityLevel: 'county' as EntityLevel,
  uploadResponse: null,
  columnMappings: [],
  targetTable: 'indicator_values' as TargetTable,
  keyColumn: '',
  keyType: 'ags_5' as KeyType,
  selectedTemplate: null,
  referenceYear: currentYear,
  editionMode: 'update_existing' as EditionMode,  // CSV imports always attach to existing GV100 editions
  editionName: '',
  targetEditionId: null,
  saveAsTemplate: null,
  templateProvider: 'custom',
  importResult: null,
  jobId: null,
  isUploading: false,
  isImporting: false,
  error: null,
}

export const useCSVImportStore = create<CSVImportState>((set) => ({
  ...initialState,

  setStep: (step) => set({ step }),
  setFile: (file) => set({ file }),
  setEntityLevel: (entityLevel) => set({ entityLevel }),
  setUploadResponse: (uploadResponse) => {
    if (uploadResponse) {
      // Initialize column mappings from upload response
      const columnMappings: ColumnMappingConfig[] = uploadResponse.columns.map((col) => ({
        csv_column: col.name,
        target: col.suggested_mapping || '_skip',
        data_type: col.detected_type,
        transform: null,
        skip: !col.suggested_mapping,
      }))

      set({
        uploadResponse,
        columnMappings,
        keyColumn: uploadResponse.suggested_key_column || '',
        keyType: uploadResponse.suggested_key_type || 'ags_5',
        selectedTemplate: uploadResponse.suggested_template,
        // Set entity level from auto-detection
        entityLevel: uploadResponse.detected_entity_level || 'county',
      })
    } else {
      set({ uploadResponse: null, columnMappings: [] })
    }
  },
  setColumnMappings: (columnMappings) => set({ columnMappings }),
  updateColumnMapping: (index, mapping) =>
    set((state) => {
      const newMappings = [...state.columnMappings]
      newMappings[index] = { ...newMappings[index], ...mapping }
      return { columnMappings: newMappings }
    }),
  setTargetTable: (targetTable) => set({ targetTable }),
  setKeyColumn: (keyColumn) => set({ keyColumn }),
  setKeyType: (keyType) => set({ keyType }),
  setSelectedTemplate: (selectedTemplate) => set({ selectedTemplate }),
  applyTemplate: (template, mappings) => {
    set((state) => {
      // Apply template mappings to current columns
      const newMappings = state.columnMappings.map((col) => {
        const templateMapping = mappings[col.csv_column]
        if (templateMapping) {
          return {
            ...col,
            target: templateMapping.target,
            data_type: templateMapping.data_type || col.data_type,
            transform: templateMapping.transform,
            skip: templateMapping.skip || templateMapping.target === '_skip',
          }
        }
        return col
      })

      return {
        selectedTemplate: template,
        columnMappings: newMappings,
        targetTable: template.target_table,
      }
    })
  },
  setReferenceYear: (referenceYear) => set({ referenceYear }),
  setEditionMode: (editionMode) => set({ editionMode }),
  setEditionName: (editionName) => set({ editionName }),
  setTargetEditionId: (targetEditionId) => set({ targetEditionId }),
  setSaveAsTemplate: (saveAsTemplate) => set({ saveAsTemplate }),
  setTemplateProvider: (templateProvider) => set({ templateProvider }),
  setImportResult: (importResult) => set({ importResult }),
  setJobId: (jobId) => set({ jobId }),
  setUploading: (isUploading) => set({ isUploading }),
  setImporting: (isImporting) => set({ isImporting }),
  setError: (error) => set({ error }),
  reset: () => set(initialState),
}))
